import { Button, Intent } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import React from 'react'
import { TextWithOverflowTooltip } from '../../../../../components/TextWithOverflowTooltip'
import { UprightStyle } from '../../../../../types'
import { ProductOption, ProductVariant } from '../../../../../types/api'
import OptionModal from './OptionModal'
import VariantEditor from './VariantEditor'
import { FormError } from '../types'

const styles: UprightStyle = {
  tableSpacing: { borderSpacing: '0 17px' },
  header: { marginBottom: 20 },
  sectionHeader: { marginTop: `30px` },
  sectionText: { marginBottom: 30 },
  optionNameCell: { paddingRight: 5, display: 'flex', gap: 5 }
}

interface Props {
  product: any
  onProductChange: any
  onVariantChange: any
  variants: ProductVariant[]
  errors: FormError
  draft: boolean
  sectionOpen?: boolean
}

const VariantsSection = ({ product, onProductChange, onVariantChange, errors, sectionOpen, draft = false }: Props) => {
  const [optionModalOpen, setOptionModalOpen] = React.useState(false)
  const productOptionCount = (product.options || []).length
  const productOverridesActive = !!product.overriddenValues

  return (
    <>
      {optionModalOpen && (
        <OptionModal
          // BP will keep this mounted and persist state between open/closes
          // We want this component to reset each time its opened
          dialogProps={{
            isOpen: true,
            onClose: () => setOptionModalOpen(false)
          }}
          onChange={onProductChange}
          productOptions={product.options || []}
          variants={product.variants}
          productOrderCount={product.orders?.length || 0}
        />
      )}

      <h5 className="section-header">Options</h5>
      <p style={styles.header}>
        Add up to three options to express differences across the product (size, color, material etc.)
      </p>

      {product.variants?.length > 0 && (
        <table className="table-reset table-variant-options" style={styles.tableSpacing}>
          <tr>
            <td>Total number of variants</td>
            <td>
              <strong>{product.variants?.length}</strong>
            </td>
          </tr>
          {/* Options may or may not have a unique ID, the index is good enough */}
          {/* eslint-disable react/no-array-index-key */}
          {product.options?.map((po: ProductOption, idx: number) => {
            const values = po.values.join(', ')
            return (
              <tr key={idx}>
                <td style={styles.optionNameCell}>
                  Option {idx + 1}:
                  <TextWithOverflowTooltip label={<strong>{po.name}</strong>} tooltipContent={po.name} />
                </td>
                <td>
                  <TextWithOverflowTooltip label={<strong>{values}</strong>} tooltipContent={values} />
                </td>
              </tr>
            )
          })}
        </table>
      )}

      <Button
        className="reset-button-spacing"
        icon={productOptionCount >= 0 ? IconNames.EDIT : IconNames.ADD}
        intent={Intent.PRIMARY}
        minimal
        onClick={() => setOptionModalOpen(true)}
        disabled={!!product?.overriddenValues}
        title={
          product?.overriddenValues ? 'Price overrides active for this product, remove it to update variants' : null
        }
      >
        {productOptionCount > 0 ? 'Edit options' : 'Add option'}
      </Button>

      {product.variants?.length > 0 && (
        <>
          <h5 className="section-header" style={styles.sectionHeader}>
            Variants
          </h5>
          <p style={styles.sectionText}>Unique combinations of option values</p>
          {product.variants.length > 0 && (
            <VariantEditor
              errors={errors}
              onVariantChange={onVariantChange}
              variants={product.variants || []}
              draft={draft}
              showGutterTooltipErrors={sectionOpen}
              productOverridesActive={productOverridesActive}
            />
          )}
        </>
      )}
    </>
  )
}

export default VariantsSection
